import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { t, tryT, type MessageKey } from '@reos/i18n';
import { EmptyState } from '@redhand/ui';
import { PageHead } from '../components/page-head.js';
import { ListTable } from '../components/list-table.js';
import {
  exportDownloadUrl,
  listAuditCases,
  listOfficeExports,
  type DdCaseItem,
  type DdExportItem,
} from '../lib/dd-exports-api.js';

/**
 * `/reporty` — Reporty (krok T5, bod 3 auditu klikacího mockupu).
 *
 * Do T4 byla položka PŘIPRAVUJE SE. Data přitom existují: exporty reportů
 * vznikají krokem O5b a leží v `dd_exports`. Tahle obrazovka je jen ukazuje
 * napříč zakázkami kanceláře a nabízí stažení hotového PDF — nic negeneruje,
 * report se pořád zakládá v zakázce.
 *
 * Názvy druhů a stavů se překládají katalogem; strojový kód z API se do UI
 * nedostane ani když přibude nový druh (`tryT` → fallback na kód).
 */

const LOCALE = 'cs' as const;

function kindLabel(kind: string): string {
  return tryT(`webAdmin.reports.kind.${kind}` as MessageKey, { locale: LOCALE }) ?? kind;
}

function statusLabel(status: string): string {
  return tryT(`webAdmin.reports.status.${status}` as MessageKey, { locale: LOCALE }) ?? status;
}

function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

function formatSize(bytes: number | null): string {
  if (bytes === null) return '';
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

/**
 * Krok UXF2 bod 1 (nalez #23, N2) — RADEK ZACINA NEMOVITOSTI.
 *
 * Dve PDF stejneho druhu k ruznym domum se do tohoto kroku lisila jen casem
 * vzniku. Prvni sloupec proto nese ADRESU auditniho pripadu a pod ni odkaz na
 * zakazku; hleda se taky podle adresy.
 *
 * ROZHODNUTI UXF2-1: „cislo zakazky" ze zadani data NEMAJI — zakazka ma nazev
 * (casto prave adresu), lidske cislo v `cases` neexistuje. Misto vymysleneho
 * cisla stoji v radku adresa a odkaz „Zakázka"; interni UUID se makleri
 * neukazuje (tentyz zakaz jako `lib/case-title.ts`).
 */
export function reportPropertyLine(
  row: Pick<DdExportItem, 'caseId'>,
  caseById: ReadonlyMap<string, DdCaseItem>,
): { readonly address: string | null; readonly dealCaseId: string | null } {
  const ddCase = caseById.get(row.caseId);
  const address = ddCase?.addressQuery.trim();
  return {
    address: address === undefined || address === '' ? null : address,
    dealCaseId: ddCase?.caseId ?? null,
  };
}

/** Hledani podle adresy (bod 1 zadani) — nad TIMTEZ retezcem, ktery je v radku. */
export function filterReportsByAddress(
  rows: readonly DdExportItem[],
  caseById: ReadonlyMap<string, DdCaseItem>,
  query: string,
): readonly DdExportItem[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return rows;
  return rows.filter((row) =>
    (reportPropertyLine(row, caseById).address ?? '').toLowerCase().includes(needle),
  );
}

export function ReportyPage(): React.JSX.Element {
  const [rows, setRows] = React.useState<DdExportItem[] | null>(null);
  const [caseById, setCaseById] = React.useState<ReadonlyMap<string, DdCaseItem>>(new Map());
  const [query, setQuery] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    listOfficeExports().then(
      (items) => {
        if (!cancelled) setRows(items);
      },
      () => {
        if (!cancelled) setError(t('webAdmin.contracts.loadError', { locale: LOCALE }));
      },
    );
    // Adresy jsou DOPLNUJICI informace: kdyz seznam pripadu selze, seznam
    // reportu zustane (jen bez adres), ne cela obrazovka v chybe.
    listAuditCases().then(
      (items) => {
        if (!cancelled) setCaseById(new Map(items.map((item) => [item.id, item])));
      },
      () => {
        /* ponechame prazdnou mapu */
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleRows = rows === null ? [] : filterReportsByAddress(rows, caseById, query);

  return (
    <div data-testid="reports-page">
      <PageHead
        title={t('webAdmin.reports.title', { locale: LOCALE })}
        subtitle={t('webAdmin.reports.perex', { locale: LOCALE })}
      />
      <div className="p-6">
        {error !== null && <p role="alert">{error}</p>}
        {rows !== null && rows.length > 0 && (
          <div className="rh-a2-filters">
            <div className="rh-a2-search" style={{ maxWidth: 320 }}>
              <span className="rh-a2-ico rh-a2-ico--sm rh-a2-search__icon" aria-hidden="true">
                <Search size={15} strokeWidth={1.75} />
              </span>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('webAdmin.reports.searchPlaceholder', { locale: LOCALE })}
                aria-label={t('webAdmin.reports.searchPlaceholder', { locale: LOCALE })}
                data-testid="reports-search"
              />
            </div>
          </div>
        )}
        {rows !== null && rows.length === 0 && error === null && (
          <EmptyState title={t('webAdmin.reports.empty', { locale: LOCALE })} />
        )}
        {rows !== null && rows.length > 0 && visibleRows.length === 0 && (
          <EmptyState title={t('webAdmin.reports.searchEmpty', { locale: LOCALE })} />
        )}
        {rows !== null && visibleRows.length > 0 && (
          <ListTable
            testId="reports-table"
            caption={t('webAdmin.reports.title', { locale: LOCALE })}
            rows={visibleRows}
            rowKey={(row) => row.id}
            rowTestId={(row) => `reports-row-${row.id}`}
            columns={[
              {
                key: 'property',
                label: t('webAdmin.reports.colProperty', { locale: LOCALE }),
                cell: (row) => {
                  const line = reportPropertyLine(row, caseById);
                  return (
                    <>
                      <span className="font-medium" data-testid={`reports-property-${row.id}`}>
                        {line.address ?? t('webAdmin.reports.propertyUnknown', { locale: LOCALE })}
                      </span>
                      {line.dealCaseId === null ? null : (
                        <Link
                          to="/zakazky/$id"
                          params={{ id: line.dealCaseId }}
                          className="rh-a2-sub"
                          data-testid={`reports-case-link-${row.id}`}
                        >
                          {t('webAdmin.reports.caseLink', { locale: LOCALE })}
                        </Link>
                      )}
                    </>
                  );
                },
              },
              {
                key: 'kind',
                label: t('webAdmin.reports.colKind', { locale: LOCALE }),
                cell: (row) => <span data-testid={`reports-kind-${row.id}`}>{kindLabel(row.kind)}</span>,
              },
              {
                key: 'status',
                label: t('webAdmin.reports.colStatus', { locale: LOCALE }),
                cell: (row) => <span className="rh-badge">{statusLabel(row.status)}</span>,
              },
              {
                key: 'created',
                label: t('webAdmin.reports.colCreated', { locale: LOCALE }),
                cell: (row) => formatDateTime(row.createdAt),
              },
              {
                key: 'size',
                label: t('webAdmin.reports.colSize', { locale: LOCALE }),
                cell: (row) => formatSize(row.sizeBytes),
              },
              {
                key: 'actions',
                label: t('webAdmin.contracts.list.colActions', { locale: LOCALE }),
                srOnlyLabel: true,
                cell: (row) =>
                  row.downloadPath === null ? (
                    <span className="reos-template-card__meta">
                      {t('webAdmin.reports.notReady', { locale: LOCALE })}
                    </span>
                  ) : (
                    <a
                      className="rh-btn"
                      href={exportDownloadUrl(row.downloadPath)}
                      data-testid={`reports-download-${row.id}`}
                    >
                      {t('webAdmin.reports.download', { locale: LOCALE })}
                    </a>
                  ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
